import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'react-toastify';
import { assets } from '../../assets/assets';
import './ResetPassword.css';

const ResetPassword = () => {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [step, setStep] = useState('request'); // 'request' or 'reset'
    const [email, setEmail] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [token, setToken] = useState('');
    const [tokenValid, setTokenValid] = useState(false);

    // Dynamic URL based on environment
    const getApiUrl = () => {
        const hostname = window.location.hostname;
        const isDevelopment = hostname === 'localhost' || hostname === '127.0.0.1';
        
        if (isDevelopment) {
            // Use the proxy in development to avoid CORS issues
            return '';
        } else {
            // Remove trailing slash if present to avoid double slashes
            const backendUrl = (import.meta.env.VITE_BACKEND_URL || 'https://dashbasket.onrender.com');
            return backendUrl.replace(/\/$/, '');
        }
    };
    
    const url = getApiUrl();

    useEffect(() => {
        const tokenParam = searchParams.get('token');
        if (tokenParam) {
            setToken(tokenParam);
            setStep('reset');
            verifyToken(tokenParam);
        }
    }, [searchParams]);

    const verifyToken = async (tokenToVerify) => {
        try {
            const response = await axios.get(`${url}/api/user/verify-reset-token/${tokenToVerify}/customer`);
            if (response.data.success) {
                setTokenValid(true);
                setEmail(response.data.email);
            } else {
                setTokenValid(false);
                toast.error(t('resetPassword.invalid_or_expired_link'));
            }
        } catch (error) {
            setTokenValid(false);
            toast.error(t('resetPassword.invalid_or_expired_link'));
        }
    };

    const handleRequestReset = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        try {
            const response = await axios.post(`${url}/api/user/request-password-reset`, {
                email,
                userType: 'customer'
            });

            if (response.data.success) {
                toast.success(response.data.message);
                setEmail('');
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            console.error('Password reset request error:', error);
            toast.error(error.response?.data?.message || t('resetPassword.failed_send_reset_email'));
        } finally {
            setIsLoading(false);
        }
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        
        if (newPassword !== confirmPassword) {
            toast.error(t('resetPassword.passwords_do_not_match'));
            return;
        }

        if (newPassword.length < 6) {
            toast.error(t('resetPassword.password_min_length'));
            return;
        }

        setIsLoading(true);

        try {
            const response = await axios.post(`${url}/api/user/reset-password`, {
                token,
                newPassword,
                userType: 'customer'
            });

            if (response.data.success) {
                toast.success(t('resetPassword.password_reset_successfully'));
                navigate('/');
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            console.error('Password reset error:', error);
            toast.error(error.response?.data?.message || t('resetPassword.failed_reset_password'));
        } finally {
            setIsLoading(false);
        }
    };

    if (step === 'reset' && !tokenValid) {
        return (
            <div className="reset-password-container">
                <div className="reset-password-card">
                    <div className="reset-password-header">
                        <img src={assets.logo} alt="DASHBASKET Logo" />
                        <h1>{t('resetPassword.invalid_reset_link')}</h1>
                        <p>{t('resetPassword.link_invalid_or_expired')}</p>
                    </div>
                    <div className="reset-password-actions">
                        <button 
                            onClick={() => navigate('/')}
                            className="btn btn-primary"
                        >
                            {t('resetPassword.back_to_home')}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="reset-password-container">
            <div className="reset-password-card">
                <div className="reset-password-header">
                    <img src={assets.logo} alt="DASHBASKET Logo" />
                    <h1>{step === 'request' ? t('resetPassword.reset_password') : t('resetPassword.set_new_password')}</h1>
                    <p>
                        {step === 'request' 
                            ? t('resetPassword.enter_email_instruction')
                            : t('resetPassword.enter_new_password_instruction')
                        }
                    </p>
                </div>

                {step === 'request' ? (
                    <form onSubmit={handleRequestReset} className="reset-password-form">
                        <div className="form-group">
                            <label htmlFor="email">{t('resetPassword.email_address')}</label>
                            <input
                                type="email"
                                id="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder={t('resetPassword.enter_your_email')}
                                required
                            />
                        </div>

                        <button 
                            type="submit" 
                            className="btn btn-primary btn-block"
                            disabled={isLoading}
                        >
                            {isLoading ? t('resetPassword.sending') : t('resetPassword.send_reset_link')}
                        </button>

                        <div className="reset-password-actions">
                            <button 
                                type="button"
                                onClick={() => navigate('/')}
                                className="btn btn-secondary"
                            >
                                {t('resetPassword.back_to_login')}
                            </button>
                        </div>
                    </form>
                ) : (
                    <form onSubmit={handleResetPassword} className="reset-password-form">
                        <div className="form-group">
                            <label htmlFor="newPassword">{t('resetPassword.new_password')}</label>
                            <div className="password-input-container">
                                <input
                                    type={showPassword ? "text" : "password"}
                                    id="newPassword"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    placeholder={t('resetPassword.enter_new_password')}
                                    required
                                />
                                <button 
                                    type="button" 
                                    className="password-toggle-btn"
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    {showPassword ? (
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M3 3l18 18M10.5 10.5a3 3 0 1 0 4.24 4.24m1.06-5.76a3 3 0 0 0-5.3-5.3M12 5c-4.48 0-8.4 2.8-10 7 .6 1.6 1.6 3 2.8 4.1m2.2 1.4c1.6.6 3.3.9 5 .9 4.48 0 8.4-2.8 10-7-.7-1.8-1.8-3.3-3.1-4.6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    ) : (
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    )}
                                </button>
                            </div>
                        </div>

                        <div className="form-group">
                            <label htmlFor="confirmPassword">{t('resetPassword.confirm_new_password')}</label>
                            <div className="password-input-container">
                                <input
                                    type={showConfirmPassword ? "text" : "password"}
                                    id="confirmPassword"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    placeholder={t('resetPassword.confirm_new_password_placeholder')}
                                    required
                                />
                                <button 
                                    type="button" 
                                    className="password-toggle-btn"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                >
                                    {showConfirmPassword ? (
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M3 3l18 18M10.5 10.5a3 3 0 1 0 4.24 4.24m1.06-5.76a3 3 0 0 0-5.3-5.3M12 5c-4.48 0-8.4 2.8-10 7 .6 1.6 1.6 3 2.8 4.1m2.2 1.4c1.6.6 3.3.9 5 .9 4.48 0 8.4-2.8 10-7-.7-1.8-1.8-3.3-3.1-4.6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    ) : (
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    )}
                                </button>
                            </div>
                        </div>

                        <button 
                            type="submit" 
                            className="btn btn-primary btn-block"
                            disabled={isLoading}
                        >
                            {isLoading ? t('resetPassword.resetting') : t('resetPassword.reset_password')}
                        </button>

                        <div className="reset-password-actions">
                            <button 
                                type="button"
                                onClick={() => navigate('/')}
                                className="btn btn-secondary"
                            >
                                {t('resetPassword.back_to_login')}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default ResetPassword; 